import { ChevronRight } from 'lucide-react';
import * as React from 'react';

import type { CampaignDetailDto } from '@/api/campaign-client';
import {
  useAddClientScope,
  useRemoveClientScope,
  useSetClientsDiffusion,
} from '@/api/use-campaigns';
import { useClientsList } from '@/api/use-clients';
import { cn } from '@/lib/utils';
import { ClientsBeneficiariesPanel } from '@/routes/admin/clients/clients-beneficiaries-panel';
import { formatSiren, SideCard } from '@/routes/admin/campaigns/detail-ui';
import {
  EntityScopePicker,
  type ScopeCandidate,
  type ScopeMember,
} from '@/routes/admin/campaigns/entity-scope-picker';

// =============================================================================
// clients-panel.tsx — Carte « Clients bénéficiaires » du détail campagne (colonne droite).
// =============================================================================
// Regroupe trois fonctions existantes, comportement conservé (refonte 2026-09-16) :
//   1. Périmètre clients : liste déroulante AUTOFILTRANTE multi-sélection (lot F1-bis 2026-09-21),
//      ajout MULTIPLE (appels unitaires enchaînés, l'API n'ayant pas d'endpoint de lot) et retrait
//      unitaire, inchangé.
//   2. B4 — diffusion des noms de clients (useSetClientsDiffusion). GATE_HUMAN 2026-09-16 (Q6) : le segmenté
//      « Masqués | Visibles » sous « Noms visibles par les fournisseurs » remplace la case « Autoriser
//      l'affichage des noms de clients », même fonction.
//   3. S33 — liste des bénéficiaires telle que la voient les fournisseurs (ClientsBeneficiariesPanel,
//      autorité API du masquage) : dépliable (GATE_HUMAN Q8 « Liste dépliable »).
//
// WCAG 2.1 AA : segmenté en boutons aria-pressed dans un groupe nommé, état doublé d'un texte (role=status),
// dépliable en bouton aria-expanded + aria-controls.
//
// LOT F1-bis (GATE_HUMAN 2026-09-21) — le SEUIL de deux caractères et le PLAFOND de 50 résultats
// disparaissent : le référentiel clients est déjà chargé côté admin (`useClientsList`), la liste
// déroulante s'ouvre donc sur son intégralité, triée alphabétiquement par `FilterCombo`, et défile.
// Les clients DÉJÀ au périmètre restent exclus des candidats (comportement conservé).

export function ClientsPanel({
  campaign,
}: {
  readonly campaign: CampaignDetailDto;
}): React.JSX.Element {
  const add = useAddClientScope();
  const remove = useRemoveClientScope();
  const clients = useClientsList();

  // Résolution SIREN → dénomination (référentiel clients complet, chargé côté admin).
  const nameBySiren = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const c of clients.data ?? []) {
      m.set(c.siren, c.raisonSociale);
    }
    return m;
  }, [clients.data]);

  const addedSirens = React.useMemo(
    () => new Set(campaign.clientScopes.map((s) => s.clientSiren)),
    [campaign.clientScopes],
  );

  // Candidats = TOUT le référentiel moins les membres du périmètre. Ni seuil de frappe, ni plafond :
  // le filtrage (casse et accents) et le tri sont l'affaire de `FilterCombo`, la recherche par SIREN
  // reste possible via le `hint` que le picker pose sur chaque option.
  const candidates = React.useMemo<readonly ScopeCandidate[]>(
    () =>
      (clients.data ?? [])
        .filter((c) => !addedSirens.has(c.siren))
        .map((c) => ({ id: c.siren, denomination: c.raisonSociale })),
    [clients.data, addedSirens],
  );

  const members = React.useMemo<readonly ScopeMember[]>(
    () =>
      campaign.clientScopes.map((s) => ({
        entryId: s.scopeId,
        id: s.clientSiren,
        denomination: nameBySiren.get(s.clientSiren) ?? s.clientSiren,
      })),
    [campaign.clientScopes, nameBySiren],
  );

  return (
    <SideCard
      headingId="detail-clients-heading"
      label={`Clients bénéficiaires · ${String(campaign.clientScopes.length)}`}
    >
      <EntityScopePicker
        variant="card"
        entityNoun="client"
        pickerLabel="Clients à ajouter"
        noun={{ singular: 'client', plural: 'clients', pastParticiple: 'ajouté' }}
        idLabel="SIREN"
        formatId={formatSiren}
        searchPlaceholder="Rechercher un client…"
        candidates={candidates}
        // L'API n'a PAS d'ajout en lot pour le périmètre clients : le picker enchaîne ces appels
        // unitaires séquentiellement et rend compte entrée par entrée (lib/sequential-add.ts).
        onAdd={(clientSiren) =>
          add.mutateAsync({ campaignId: campaign.campaignId, body: { clientSiren } })
        }
        members={members}
        onRemove={(entryId) => {
          remove.mutate({ campaignId: campaign.campaignId, entryId });
        }}
        removePending={remove.isPending}
        emptyMembersText="Aucun client dans le périmètre."
      />

      <div className="border-t border-border pt-4">
        <ClientsDiffusionControl campaign={campaign} />
      </div>
    </SideCard>
  );
}

/**
 * B4 — diffusion des noms de clients. Reflète l'état COURANT `campaign.clientsDiffusionEnabled` et appelle
 * `useSetClientsDiffusion` au changement. L'autorité (persistance + diffusion effective via le carrier ACL)
 * reste l'API ; ce contrôle ne fait que relayer l'intention.
 */
function ClientsDiffusionControl({
  campaign,
}: {
  readonly campaign: CampaignDetailDto;
}): React.JSX.Element {
  const setDiffusion = useSetClientsDiffusion();
  const enabled = campaign.clientsDiffusionEnabled;
  const labelId = `clients-diffusion-${campaign.campaignId}`;
  const [expanded, setExpanded] = React.useState(false);
  const listId = `clients-diffusion-list-${campaign.campaignId}`;

  const choose = (next: boolean): void => {
    if (next === enabled) {
      return;
    }
    setDiffusion.mutate({ campaignId: campaign.campaignId, enabled: next });
  };

  const segment = (active: boolean): string =>
    cn(
      'h-8 px-3 text-sm font-medium transition-colors focus-visible:relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
      active
        ? 'bg-foreground text-background'
        : 'bg-background text-muted-foreground hover:text-foreground',
    );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span id={labelId} className="text-sm font-medium">
          Noms visibles par les fournisseurs
        </span>
        <div
          role="group"
          aria-labelledby={labelId}
          className="inline-flex overflow-hidden rounded-md border border-input"
        >
          <button
            type="button"
            aria-pressed={!enabled}
            disabled={setDiffusion.isPending}
            onClick={() => choose(false)}
            className={segment(!enabled)}
          >
            Masqués
          </button>
          <button
            type="button"
            aria-pressed={enabled}
            disabled={setDiffusion.isPending}
            onClick={() => choose(true)}
            className={cn(segment(enabled), 'border-l border-input')}
          >
            Visibles
          </button>
        </div>
      </div>
      <p role="status" className="text-xs text-muted-foreground">
        {enabled
          ? 'Côté fournisseur : « les raisons sociales des clients bénéficiaires sont affichées ».'
          : 'Côté fournisseur : « les noms seront communiqués ultérieurement ».'}
      </p>
      {setDiffusion.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {setDiffusion.error.message}
        </p>
      ) : null}

      {/* S33-CLIENTS-DIFFUSION (DBU §3.3) — bénéficiaires présentés en OUTPUT, noms diffusés sous réserve du
          flag admin. Conteneur auto-porté (client HTTP réel), monté au dépliage. */}
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={listId}
        onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center gap-1 rounded-sm text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ChevronRight
          aria-hidden="true"
          className={cn('size-4 transition-transform', expanded ? 'rotate-90' : undefined)}
        />
        Liste vue par les fournisseurs
      </button>
      <div id={listId} hidden={!expanded}>
        {expanded ? <ClientsBeneficiariesPanel campaignId={campaign.campaignId} /> : null}
      </div>
    </div>
  );
}
